export const darkenHexColor = (hex: string, percent: number): string => {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, '');

  // Parse r, g, b values
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Calculate the darkening effect
  r = Math.floor(r * (1 - percent / 100));
  g = Math.floor(g * (1 - percent / 100));
  b = Math.floor(b * (1 - percent / 100));

  // Ensure values are within 0-255 range
  r = r < 0 ? 0 : r;
  g = g < 0 ? 0 : g;
  b = b < 0 ? 0 : b;

  // Convert back to hex and ensure 2 digits
  const rr =
    r.toString(16).length === 1 ? '0' + r.toString(16) : r.toString(16);
  const gg =
    g.toString(16).length === 1 ? '0' + g.toString(16) : g.toString(16);
  const bb =
    b.toString(16).length === 1 ? '0' + b.toString(16) : b.toString(16);

  return `#${rr}${gg}${bb}`;
};

interface Rgb {
  r: number;
  g: number;
  b: number;
}

const clamp255 = (value: number): number =>
  value > 255 ? 255 : value < 0 ? 0 : value;

/**
 * `#rgb` / `#rgba` / `#rrggbb` / `#rrggbbaa`, with or without the hash and in
 * any casing, to a lowercase `#rrggbb`. Returns null for anything else, so
 * callers can fall back instead of rendering a broken colour.
 *
 * The alpha channel is dropped: the shapes that carry one come from the legacy
 * ColorPicker, which always emits `hexa`, and a theme accent is opaque.
 */
export const normalizeHex = (input: string): string | null => {
  const raw = (input ?? '').trim().replace(/^#/, '');
  if (/^[0-9a-f]{3,4}$/i.test(raw)) {
    return `#${raw
      .slice(0, 3)
      .split('')
      .map((char) => char + char)
      .join('')}`.toLowerCase();
  }
  return /^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(raw)
    ? `#${raw.slice(0, 6).toLowerCase()}`
    : null;
};

const hexToRgb = (hex: string): Rgb => {
  const raw = hex.replace(/^#/, '');
  return {
    r: parseInt(raw.substring(0, 2), 16),
    g: parseInt(raw.substring(2, 4), 16),
    b: parseInt(raw.substring(4, 6), 16),
  };
};

const rgbToHex = ({ r, g, b }: Rgb): string =>
  `#${[r, g, b]
    .map((channel) =>
      Math.round(clamp255(channel)).toString(16).padStart(2, '0'),
    )
    .join('')}`;

/**
 * `hex` blended towards white. `amount` is how much white: 0 returns the colour
 * untouched, 1 returns white. The tints every skin stylesheet spells out by hand
 * (`--minthr-color-light-primary*`, the `-opacity` pairs) are this function at a
 * few fixed ratios.
 */
export const mixWithWhite = (hex: string, amount: number): string => {
  const { r, g, b } = hexToRgb(normalizeHex(hex) ?? '#000000');
  const blend = (channel: number): number => channel + (255 - channel) * amount;
  return rgbToHex({ r: blend(r), g: blend(g), b: blend(b) });
};

/*
 * ---------------------------------------------------------------------------
 * Hex -> CSS filter
 * ---------------------------------------------------------------------------
 * The legacy chrome recolours its flat <img> icons with a filter chain that
 * starts `brightness(0) saturate(100%)` — i.e. paint the icon black, then push
 * that black to the accent. Every skin.*.css has such a chain baked in, produced
 * by hand for that skin's accent. A client-picked accent has no baked chain, so
 * we solve for one: CSS `hue-rotate` is a linear matrix approximation with no
 * inverse, so the standard approach is numeric — walk invert/sepia/saturate/
 * hue-rotate/brightness/contrast towards the target with SPSA.
 *
 * The walk is seeded from the target colour rather than Math.random, so the same
 * hex always yields the same chain: a theme must not shift between reloads.
 */

const multiply = (color: Rgb, m: number[]): void => {
  const { r, g, b } = color;
  color.r = clamp255(r * m[0] + g * m[1] + b * m[2]);
  color.g = clamp255(r * m[3] + g * m[4] + b * m[5]);
  color.b = clamp255(r * m[6] + g * m[7] + b * m[8]);
};

const sepia = (color: Rgb, value: number): void => {
  const rest = 1 - value;
  multiply(color, [
    0.393 + 0.607 * rest,
    0.769 - 0.769 * rest,
    0.189 - 0.189 * rest,
    0.349 - 0.349 * rest,
    0.686 + 0.314 * rest,
    0.168 - 0.168 * rest,
    0.272 - 0.272 * rest,
    0.534 - 0.534 * rest,
    0.131 + 0.869 * rest,
  ]);
};

const saturate = (color: Rgb, value: number): void => {
  multiply(color, [
    0.213 + 0.787 * value,
    0.715 - 0.715 * value,
    0.072 - 0.072 * value,
    0.213 - 0.213 * value,
    0.715 + 0.285 * value,
    0.072 - 0.072 * value,
    0.213 - 0.213 * value,
    0.715 - 0.715 * value,
    0.072 + 0.928 * value,
  ]);
};

const hueRotate = (color: Rgb, angle: number): void => {
  const radians = (angle / 180) * Math.PI;
  const sin = Math.sin(radians);
  const cos = Math.cos(radians);
  multiply(color, [
    0.213 + cos * 0.787 - sin * 0.213,
    0.715 - cos * 0.715 - sin * 0.715,
    0.072 - cos * 0.072 + sin * 0.928,
    0.213 - cos * 0.213 + sin * 0.143,
    0.715 + cos * 0.285 + sin * 0.14,
    0.072 - cos * 0.072 - sin * 0.283,
    0.213 - cos * 0.213 - sin * 0.787,
    0.715 - cos * 0.715 + sin * 0.715,
    0.072 + cos * 0.928 + sin * 0.072,
  ]);
};

const linear = (color: Rgb, slope: number, intercept: number): void => {
  color.r = clamp255(color.r * slope + intercept * 255);
  color.g = clamp255(color.g * slope + intercept * 255);
  color.b = clamp255(color.b * slope + intercept * 255);
};

const invert = (color: Rgb, value: number): void => {
  const flip = (channel: number): number =>
    clamp255((value + (channel / 255) * (1 - 2 * value)) * 255);
  color.r = flip(color.r);
  color.g = flip(color.g);
  color.b = flip(color.b);
};

/** h/s/l on a 0-100 scale — the loss function weighs them alongside r/g/b. */
const toHsl = ({ r, g, b }: Rgb): { h: number; s: number; l: number } => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;

  if (max === min) return { h: 0, s: 0, l: l * 100 };

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;

  return { h: (h / 6) * 100, s: s * 100, l: l * 100 };
};

/** Per-filter bounds. Index order: invert, sepia, saturate, hue, bright, contrast. */
const clampFilter = (value: number, index: number): number => {
  const max = index === 2 ? 7500 : index === 4 || index === 5 ? 200 : 100;
  if (index === 3) {
    if (value > max) return value % max;
    return value < 0 ? max + (value % max) : value;
  }
  return value < 0 ? 0 : value > max ? max : value;
};

const seededRandom = (seed: number): (() => number) => {
  let state = seed >>> 0 || 1;
  return (): number => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
};

const applyFilters = (values: number[]): Rgb => {
  const color: Rgb = { r: 0, g: 0, b: 0 };
  invert(color, values[0] / 100);
  sepia(color, values[1] / 100);
  saturate(color, values[2] / 100);
  hueRotate(color, values[3] * 3.6);
  linear(color, values[4] / 100, 0);
  linear(color, values[5] / 100, -(0.5 * (values[5] / 100)) + 0.5);
  return color;
};

const lossAgainst = (target: Rgb, targetHsl: ReturnType<typeof toHsl>) => {
  return (values: number[]): number => {
    const color = applyFilters(values);
    const hsl = toHsl(color);
    return (
      Math.abs(color.r - target.r) +
      Math.abs(color.g - target.g) +
      Math.abs(color.b - target.b) +
      Math.abs(hsl.h - targetHsl.h) +
      Math.abs(hsl.s - targetHsl.s) +
      Math.abs(hsl.l - targetHsl.l)
    );
  };
};

const spsa = (
  loss: (values: number[]) => number,
  random: () => number,
  A: number,
  a: number[],
  c: number,
  seed: number[],
  iterations: number,
): { values: number[]; loss: number } => {
  const alpha = 1;
  const gamma = 0.16666666666666666;
  const values = seed.slice();
  const deltas = new Array<number>(6);
  const high = new Array<number>(6);
  const low = new Array<number>(6);
  let best = values.slice();
  let bestLoss = Infinity;

  for (let k = 0; k < iterations; k += 1) {
    const ck = c / Math.pow(k + 1, gamma);
    for (let i = 0; i < 6; i += 1) {
      deltas[i] = random() > 0.5 ? 1 : -1;
      high[i] = values[i] + ck * deltas[i];
      low[i] = values[i] - ck * deltas[i];
    }

    const diff = loss(high) - loss(low);
    for (let i = 0; i < 6; i += 1) {
      const gradient = (diff / (2 * ck)) * deltas[i];
      const ak = a[i] / Math.pow(A + k + 1, alpha);
      values[i] = clampFilter(values[i] - ak * gradient, i);
    }

    const current = loss(values);
    if (current < bestLoss) {
      best = values.slice();
      bestLoss = current;
    }
  }

  return { values: best, loss: bestLoss };
};

const formatFilter = (values: number[]): string => {
  const percent = (index: number): string =>
    `${Math.round(values[index])}%`;
  return [
    'brightness(0)',
    'saturate(100%)',
    `invert(${percent(0)})`,
    `sepia(${percent(1)})`,
    `saturate(${percent(2)})`,
    `hue-rotate(${Math.round(values[3] * 3.6)}deg)`,
    `brightness(${percent(4)})`,
    `contrast(${percent(5)})`,
  ].join(' ');
};

/**
 * A `filter` chain that repaints a flat black-able icon in `hex` — the same
 * shape the hand-authored skins use, so the generated rules drop straight into
 * their selectors.
 *
 * Deterministic for a given hex. Falls back to `brightness(0)` (a plain black
 * icon) if the hex is unparseable, which is legible on every surface.
 */
export const hexToCssFilter = (hex: string): string => {
  const normalized = normalizeHex(hex);
  if (!normalized) return 'brightness(0)';

  const target = hexToRgb(normalized);
  const loss = lossAgainst(target, toHsl(target));
  const random = seededRandom(target.r * 65536 + target.g * 256 + target.b + 1);

  const wideStep = [60, 180, 18000, 600, 1.2, 1.2];
  let wide = { values: [50, 20, 3750, 50, 100, 100], loss: Infinity };
  for (let attempt = 0; attempt < 3 && wide.loss > 25; attempt += 1) {
    const result = spsa(
      loss,
      random,
      5,
      wideStep,
      15,
      [50, 20, 3750, 50, 100, 100],
      500,
    );
    if (result.loss < wide.loss) wide = result;
  }

  const A = wide.loss + 1;
  const narrow = spsa(
    loss,
    random,
    wide.loss,
    [0.25 * A, 0.25 * A, A, 0.25 * A, 0.2 * A, 0.2 * A],
    2,
    wide.values,
    500,
  );

  return formatFilter(narrow.loss < wide.loss ? narrow.values : wide.values);
};
